export const TERMS_META = {
  version: "v0.2",
  lastUpdated: "Dec 2025",
  companyName: "Bodyown Limited",
  companyNumber: "15113000",
  registeredAddress: "85 Great Portland Street, First Floor, London, W1W 7LT",
  email: "info@bodyown.health",
  website: "https://bodyown.health",
  governingLaw: "England & Wales",
};

export const TERMS_INTRO = {
  company:
    "We are Bodyown Limited, a company incorporated in England and Wales (No. 15113000), registered at 85 Great Portland Street, First Floor, London, W1W 7LT.",
  audience:
    "The App is intended to assist and support postnatal women in the United Kingdom. You must be 18 years or over to use the App and create an account.",
  badges: "UK-based · Postnatal Support · Not a medical service",
};

export const TERMS_EMERGENCY = {
  title: "In an emergency",
  body: "Call 999 or attend A&E immediately. For urgent non-emergency advice, call NHS 111 or contact your GP/midwife. Do not rely on this App for urgent matters.",
};

export const TERMS_SECTIONS = [
  {
    id: "important",
    title: "Important Information",
    content: [
      "BodyOwn provides educational information and guided activities to support postnatal recovery. Our App does not provide medical advice, diagnosis or treatment, and should not be considered such.",
      "BodyOwn is not an emergency service, and we do not monitor your health or journal entries in real time. If you or your baby requires urgent medical assistance, call 999 or attend A&E without delay. For urgent advice when it is not an emergency, call NHS 111 or contact your GP/midwife.",
      "Always seek advice from a qualified healthcare professional before acting on information provided in the App, particularly if you have symptoms, complications or concerns.",
      "Where the App signposts you to third-party services (including NHS and, in future, private healthcare providers), those services are provided by third parties on their own terms and BodyOwn takes no responsibility nor accepts any liability in respect of any services provided by third parties.",
    ],
  },
  {
    id: "agreement",
    title: "1. This Agreement",
    content: [
      "The term 'App store' means any third-party platform from which you download the App (such as the Apple App Store or Google Play). The term 'App store rules' means their applicable rules and policies.",
      "If you download the App from Apple's App Store, then the terms set out within Schedule 1 (Apple App Store terms) apply. If you download the App from Google Play, then the terms set out within Schedule 2 (Google Play terms) apply.",
      "You must comply with the terms of this agreement and any relevant App store rules, including those set out within the Schedules.",
      "You acknowledge and agree that you do not own the App or any of its contents, but you may use it on devices that you own or control.",
      "If you sell or give away the device on which you have downloaded the App, you must first remove the App from the device.",
      "You are not permitted to: modify the App's code in any way; deliberately attempt to avoid or manipulate any security features; pretend that the App is your own or make it available for others; reverse engineer, decompile or attempt to extract the source code; use automated tools to access or interact with the App; circumvent, disable or interfere with any access control or security features; or share your account password or allow anyone else to use your account.",
      "To use the App, you may need to first create an account. You must keep your account details safe and confidential.",
      "You are responsible for all activity on your account. If you think your account has been compromised, you must notify us as soon as possible at info@bodyown.health.",
    ],
  },
  {
    id: "licence",
    title: "2. Licence",
    content: [
      "We grant you a revocable, personal, non-exclusive, non-transferable licence to access and use the App for your own non-business use, subject to these terms.",
      "The licence is only for you personally and for non-business use; starts when you first access or download the App; and covers content, materials, or services accessible from, or bought in, the App including all support resources.",
    ],
  },
  {
    id: "pilot",
    title: "3. Web-App Pilot and Future Deployment",
    content: [
      "The pilot version of the App is currently made available as a web-based application accessed via a browser. We may later make the App available via App stores.",
      "The pilot version is provided for testing and feedback. Features may be incomplete, change over time, or be withdrawn. You may experience bugs, interruptions or delays. We do not guarantee that the pilot (or any feature) will be available at all times.",
      "We do not guarantee that the App will be available at all times or in all locations. Some features (including signposted services) may be UK-specific.",
    ],
  },
  {
    id: "inputs",
    title: "4. Your Inputs and Journal Entries",
    content: [
      "The App allows you to record information, including journal entries and tracker inputs. These are private to you and are not visible to other users through the App.",
      "You remain responsible for what you input into the App, and you must not include personal data about other people unless you have their permission (or another lawful basis).",
      "We will process your information in accordance with our privacy policy. You retain ownership of the information you enter into the App. You grant us a non-exclusive, worldwide, royalty-free licence to use your inputs to operate, maintain, support and improve the App.",
      "There are currently no community, public posting or user-to-user messaging features. If we introduce community features in the future, we may update these terms.",
    ],
  },
  {
    id: "ip",
    title: "5. Intellectual Property Rights",
    content: [
      "All Intellectual Property Rights in and to the App (including its design, text, graphics, images, video, audio, software, code, data compilations, underlying algorithms and all other content and materials) are owned by us or our licensors. Nothing in this agreement transfers any Intellectual Property Rights to you.",
      "The term 'BodyOwn', our logo, and any other trade marks, service marks, or trade names used in the App belong to us and you must not use them without our prior written consent, except as necessary to refer to us or our services in a fair and accurate manner.",
      "You may not copy, reproduce, republish, modify, adapt, translate, create derivative works from, distribute, license, sell, or otherwise exploit any part of the App or its content, except as expressly permitted by this agreement.",
      "If you provide us with any feedback, suggestions or ideas relating to the App, you grant us a perpetual, irrevocable, worldwide, royalty-free licence to use, modify, develop and exploit that feedback for any purpose.",
    ],
  },
  {
    id: "technical",
    title: "6. Technical Requirements",
    content: [
      "To use the App, your device and/or browser needs to meet minimum requirements including: a smartphone, tablet or computer capable of accessing the internet; iOS, Android, Windows or macOS running a supported web browser; sufficient device storage and memory; a stable internet connection and an up-to-date browser (latest or recent versions of Chrome, Safari, Edge or Firefox).",
      "The pilot web App does not support Internet Explorer or outdated browsers. JavaScript and cookies must be enabled. We may change which browsers/versions we support over time.",
    ],
  },
  {
    id: "support",
    title: "7. Support and Contact",
    content: [
      "We are responsible for customer service, maintenance and support in relation to the App. If you downloaded the App via an App store, you acknowledge that the App store has no obligation to provide any support or maintenance services.",
      "You can contact us via our support pages at https://bodyown.health, or by email at info@bodyown.health, or by post at: Bodyown Limited, 85 Great Portland Street, First Floor, London, W1W 7LT.",
      "If you have a complaint or would like to report a problem with the App, please contact us at info@bodyown.health. We will aim to respond to your complaint within 20 working days.",
    ],
  },
  {
    id: "privacy",
    title: "8. Privacy and Your Personal Information",
    content: [
      "Protecting your personal information is important to us. Our privacy policy explains what personal information we collect from you, how and why we collect, store, use and share such information, your rights in relation to it and how to contact us.",
      "We use trusted third-party service providers to help us deliver the App, including Firebase (for account authentication and database hosting). If and when we offer paid features, we may also use Stripe to process payments.",
    ],
  },
  {
    id: "health",
    title: "11. Health, Safety and Safeguarding",
    content: [
      "The App provides educational information and guided activities aimed at supporting postnatal recovery. It does not provide medical advice, diagnosis or treatment and is not a substitute for professional healthcare.",
      "You should always seek advice from a qualified healthcare professional (for example, your GP, midwife, health visitor or physiotherapist) if you have any concerns.",
      "The App is not an emergency service. If you or your baby may be in immediate danger or require urgent care, call 999 (UK) immediately. For urgent advice when it is not an emergency, call NHS 111 or contact your GP/midwife.",
      "We are not a healthcare provider and nothing in the App creates a doctor-patient relationship or any other professional relationship between you and us.",
      "We do not review your journal entries, tracker inputs or assessment results in real time and we do not provide clinical monitoring. You must not rely on the App to identify urgent symptoms or to tell you when to seek medical help.",
    ],
  },
  {
    id: "acceptable",
    title: "12. Acceptable Use",
    content: [
      "You must not use the App to: break the law or encourage any unlawful activity; send or upload anything that is defamatory, offensive, obscene or discriminatory; infringe our or anyone else's Intellectual Property Rights; transmit any harmful software code such as viruses; try to gain unauthorised access to computers, data, systems, accounts or networks; or deliberately disrupt the operation of anyone's website, App, server or business.",
    ],
  },
  {
    id: "updates",
    title: "13. Updates to the App",
    content: [
      "We may update the App from time to time for reasons that include fixing bugs or enhancing functionality. We might also change or remove functionality.",
      "We strongly suggest that you apply any updates and use the most recent version of the App as soon as they become available.",
    ],
  },
  {
    id: "changes",
    title: "14. Changes to This Agreement",
    content: [
      "We may need to revise this agreement from time to time to reflect changes in the App's functionality, to deal with a security threat or if there is a change in the law or guidance.",
      "You will be asked to agree to any material changes in advance by an in-App notification (or other prominent notice), usually when you access the App after we have made a change.",
    ],
  },
  {
    id: "payments",
    title: "16. Payments and Subscriptions",
    content: [
      "The pilot/beta release of the App is provided free of charge and there are no payments or subscriptions at this time.",
      "We may introduce paid features, subscriptions or paid trials in future. If we do, we will make clear what is included, the price, billing period and any applicable cancellation rights.",
      "If we offer subscriptions, they may renew automatically at the end of each billing period unless you cancel.",
    ],
  },
  {
    id: "liability",
    title: "17. Our Responsibility to You",
    content: [
      "If we breach this contract or are negligent, we are liable to you for foreseeable loss or damage that you suffer as a result.",
      "Nothing in these terms affects your legal rights (including under the Consumer Rights Act 2015).",
      "Nothing in these terms excludes or limits our liability for any death or personal injury caused by our negligence, liability for fraud or fraudulent misrepresentation, or any other liability that the law does not allow us to exclude or limit.",
    ],
  },
  {
    id: "governing",
    title: "22. Governing Law and Jurisdiction",
    content: [
      "The laws of England and Wales apply to this agreement, although if you are resident elsewhere you will retain the benefit of any mandatory protections given to you by the laws of that country.",
      "Any disputes will be subject to the non-exclusive jurisdiction of the courts of England and Wales.",
    ],
  },
];


 

;